<script lang="tsx" setup>
import { computed, onMounted, ref, watch } from "vue";
import {CloseBold, Filter, Refresh, View, WarnTriangleFilled, CircleCloseFilled, InfoFilled, EditPen, QuestionFilled } from "@element-plus/icons-vue"
import { useFVStore } from "@/stores/formvibes";
import { ElNotification } from 'element-plus';
import { storeToRefs } from "pinia";
import datePicker  from "../shared/date-picker.vue";
import { getDropDownActions }  from './utils';
import { clearLocalStorage, getFromLocalStorage, setToLocalStorage } from "../../stores/utils";
import Settings from './settings.vue';
import QuickExport from "./quick-export.vue";
import DeleteEntry from "./delete.vue";
import BulkStatus from "./bulk-status.vue";
import FilterControls from "./filter/filter.vue";
import { axiosPost } from "../../Axios/post";

const props = defineProps([ 'store']);
const visible = ref(false)
const store = useFVStore();
const isColSettingChanged = ref(false);
const {
		user_capability: userCapability,
		settings_saved: settingsSaved,
		entry_table_fields: entryTableFields,
	} = window.fvGlobalVar;

const { formsSelector, isPro, columns, isNoPlugins,generalStates, isEditable, dates, selectedRowsId, notice, isLoading,isSaving, currentForm, currentPlugin } = storeToRefs(store);

const prevCol = ref(JSON.parse(JSON.stringify(columns.value))); // Deep copy of columns
// const prevCol = ref('');

const i18n = window.fvGlobalVar?.i18n;

watch([notice], () => {
    ElNotification({
        offset: 30,
        dangerouslyUseHTMLString: true,
        message: `<strong>${notice.value.message}</strong>`,
        type: notice.value.isError ? 'error' : 'success',
        customClass : 'fv-items-center',
        zIndex: 99999,
        duration : 2000,
    })
})


const quickActions = ref([
    {
		value: 'quickExport',
		label: 'Quick Export',
	},
	{
		value: 'delete',
		label: 'Delete',
	},
	{
		value: 'status',
		label: 'Status',
	},
]);

const selectedForm = ref(getFromLocalStorage('fv-form'));
const selectedAction = ref('Select Action');
const storedFilters = getFromLocalStorage('fv-filters');

const showFilter = ref(storedFilters ? true : false);
const showSettings = ref(false);

const isAutoRefresh = settingsSaved?.auto_refresh;

if(isAutoRefresh){
    const auto_refresh_frequency = settingsSaved?.auto_refresh_frequency;
    setInterval(() => {
        store.fetchData();
    }, auto_refresh_frequency * 1000);
}

const isExport = computed(() => {
    return selectedAction.value == 'quickExport' ? true : false;
});

const isDelete = computed(() => {
    return selectedAction.value == 'delete' ? true : false;
});

const isStatus = computed(() => {
    return selectedAction.value == 'status' ? true : false;
});

const setFilter = () => {
    showFilter.value = !showFilter.value
}

const setShowSettings = () => {
    showSettings.value = !showSettings.value;
} 

const forms = formsSelector.value;

onMounted(async () => {
    if(!selectedForm.value){
        if (forms?.length > 0 && forms[0]?.options?.length > 0) {
            const firstGroup = forms[0];
            const firstplugin = firstGroup.label;
            const firstformObj = firstGroup.options[0];
            selectedForm.value = firstformObj.value;
            // mirror onChangeForm: persist the selected form to localStorage
            setToLocalStorage('fv-form', firstformObj);
            // set current plugin/form refs so other logic (permissions/quickActions) work
            currentPlugin.value = firstformObj.plugin || firstplugin;
            currentForm.value = firstformObj.value;
            generalStates.value.queryParams = {
                ...generalStates.value.queryParams,
                plugin: firstplugin,
                formid: firstformObj.value,
            }
        }
    }

    if(!isPro.value){
        if(currentPlugin.value === 'caldera'){
            quickActions.value = quickActions.value.filter((action) => action.value !== 'status' && action.value !== 'delete' );
        }
        else{
            quickActions.value = quickActions.value.filter((action) => action.value !== 'status');
        }
    }
    else{
        if (!userCapability?.can_delete_submissions || currentPlugin.value === 'caldera') {
            quickActions.value = quickActions.value.filter((action) => action.value !== 'delete');
        }
        if (!userCapability?.can_export_submissions) {
            quickActions.value = quickActions.value.filter((action) => action.value !== 'quickExport');
        }
        if (!userCapability?.change_fv_status || currentPlugin.value === 'caldera') {
            quickActions.value = quickActions.value.filter((action) => action.value !== 'status');
        }
    }

    await store.fetchData();
    prevCol.value = JSON.parse(JSON.stringify(columns.value));   
})

const onChangeForm = (id, e) => {
    const form = forms.filter((form) => form.label === e.label)[0].options.filter((fm) => fm.value === id)[0];
    setToLocalStorage('fv-form', form);
    const plugin = form.plugin;
    const formid = form.value;
    currentPlugin.value = plugin;
    currentForm.value = formid;

    generalStates.value = {
        ...generalStates.value,
        queryParams: {
            ...generalStates.value.queryParams,
            page_num: 1,
            plugin:
                plugin || (forms?.length > 0 ? forms[0]?.options[0]?.plugin : []),
            formid:
                formid || (forms?.length > 0 ? forms[0]?.options[0]?.value : []),
            query_filters: [
                    {
                        filter: '',
                        operator: 'LIKE',
                        key: '',
                        value: '',
                    },
                ],
            filter_relation: 'OR',
            status: [],
            selectedStatus:[],
        }
        
    }
    showFilter.value = false;
    window.localStorage.removeItem('fv-filters');
}

watch([selectedForm,() => generalStates.value.queryParams], () => {
    store.fetchData();
})

const getSubmission = async () => {
    if (isNoPlugins.value) return;
    store.fetchData({});
}

const closeDialog = () => {
    selectedAction.value = 'Select Action';
}

const closeClick = (e) => {
    showFilter.value = !showFilter.value;
}

const onSettingsSave = async () => {

    let plugin = currentPlugin.value;
    let formid = currentForm.value;
    if (!plugin || !formid) {
        const form = getFromLocalStorage('fv-form');
        if (form) {
            plugin = form.plugin;
            formid = form.value;
        } else {
            plugin = generalStates.value.queryParams.plugin;
            formid = generalStates.value.queryParams.formid;
        }
    }
    if (JSON.stringify(columns.value) === JSON.stringify(prevCol.value)) {
        return;
    }
    isSaving.value = true; 
    try {
        const { data } = await axiosPost({
            action: 'save_columns_settings',
            queryParams: {
                columns: columns.value,
                plugin: plugin,
                formid: formid,
            },
        });

        if (data.is_error) {
            throw new Error(data.message);
        }
        notice.value = {
            ...notice.value,
            isSuccess: true,
            message: data.message,
        }
        prevCol.value = JSON.parse(JSON.stringify(columns.value));
        
    } catch (error) {
        notice.value = {
            ...notice.value,
            isError: true,
            message: error.message,
        }
    } finally {
        isSaving.value = false;
        showSettings.value = false;
    }
}

const onCancelClick = () => {
    columns.value = JSON.parse(JSON.stringify(prevCol.value));
    isColSettingChanged.value = false;
    showSettings.value = false;
    
}

const groupLabel = (label) => {
    switch(label){
        case 'everest-forms':
            return 'Everest Forms';
        case 'gravity-forms':
            return 'Gravity Forms';
        case 'wp-forms':
            return 'WPForms';
        case 'caldera':
            return 'Caldera Forms';
        case 'Ninja-Forms':
            return 'Ninja Forms';
        case'ws-form':
            return 'WS Form';    
        case 'elementor':
            return 'Elementor Forms';
        case 'bricks-builder':
            return 'Bricks Builder';
        case'cf7':
            return 'Contact Form 7';        
        default:
            return label;
    }
}

</script>
<template>
    <div class="fv-content-wrapper fv-action-bar-wrapper">
        <div class="fv-page-title">
            <h3>Submissions</h3>
        </div>
        <div class="fv-action-bar">
            <div class="fv-submission-controls">
                <datePicker />
                <!-- Form Drop Down -->
                <el-select v-model="selectedForm" placeholder="Select">
                    <el-option-group
                        v-for="group in forms"
                        :key="group.label"
                        :label="groupLabel(group.label)"
                        >
                        <el-option
                            v-for="item in group.options"
                            :key="item.value"
                            :label="item.label"
                            :value="item.value"
                            @click="(e) => onChangeForm(item.value,group)"
                        />
                    </el-option-group>
                </el-select>
                <!-- Form Drop Down End -->
                <!-- Action Drop Down -->
                <el-select v-model="selectedAction" placeholder="Select">
                    <el-option
                    v-for="item in quickActions"
                    :key="item.value"
                    :label="item.label"
                    :value="item.value"
                    >
                    <el-icon > <component :is="item.icon" /></el-icon>
                    <span style="float: left">{{ item.label }}</span>
                    </el-option>
                </el-select>
                
                <el-button v-if="isPro" @click="setFilter" :icon="Filter" link size="large" :class="{'filter-active' : showFilter}" class="fv-sub-filter"/>
                
                <el-switch v-if="isPro && currentPlugin !== 'caldera' && userCapability?.can_edit_submissions" :active-action-icon="EditPen" :inactive-action-icon="EditPen" v-model="isEditable"  :class="{active : isEditable}" class="fv-edit-switch"/>
                <el-tooltip :visible="visible" auto-close="2000" v-if="isPro && currentPlugin !== 'caldera' && userCapability?.can_edit_submissions">
                    <template #content>
                        <span>Enable to Edit Submissions</span>
                        <br/>
                        <a
                            href="https://wpvibes.link/go/fv-submissions-editing/"
                            rel="noreferrer"
                            target="_blank"
                            style="display:block; color:#409eff; text-decoration: underline; text-align: center;"

                        >
                        Read More
                        </a>
                    </template>
                    <el-icon @mouseenter="visible = true" @click="visible = !visible"><QuestionFilled /></el-icon>
                </el-tooltip>
            </div>
        <div class="fv-submission-controls-settings">
            <el-button type="primary" @click="getSubmission" :icon="Refresh" link />
            <el-button @click="setShowSettings" :icon="View" link />
        </div>
        </div>
        <FilterControls v-if="showFilter" :closeClick="closeClick" />
        <el-dialog 
            v-model="showSettings" 
            :with-header="true"
            :show-close="true"
            width="560"
            class="fv-settings-dialog"
            >
            <Settings
                v-model="isColSettingChanged"
                :columns="columns"
                :prevCol="prevCol"
            />
        <template #footer>
            <el-button @click="onCancelClick">Cancel</el-button>
            <el-button :disabled="!isColSettingChanged" type="primary" @click="onSettingsSave">Save</el-button>
        </template>
        </el-dialog>
        <QuickExport :params="generalStates" :store="store" :isVisible="isExport" :close="closeDialog" />
    
        <!-- Delete Entry Dialog -->
        <DeleteEntry :isVisible="isDelete" :close="closeDialog" />
        <!-- Delete Entry Dialog End -->
        <BulkStatus :isVisible="isStatus" :close="closeDialog" />
    </div>
</template>